import { createSlice } from "@reduxjs/toolkit";
import { tabs } from "@pages/Main/Timetable/components/Tabs/tabsEnum";
import dayjs from "./dayjs.config";
import { typesEnum } from '@pages/Main/Timetable/components/Types/typesEnum';
import rolesEnum from "@/constant/rolesEnum";
import { periodModes } from "@pages/Main/Timetable/components/PeriodMode/periodModes";

// Функция для определения типа расписания
const determineTimetableType = ({filters, currentType, role = JSON.parse(localStorage.getItem("profile")).role ?? ''}) => {
    if(!role) return typesEnum.my;
    
    if (
        (role == rolesEnum.children || role == rolesEnum.parent)
        && (
            currentType === typesEnum.my
            || currentType == typesEnum.all
            || typesEnum.isAdditional(currentType)
        )
    ) {
        return currentType;
    }
    
    if (role !== rolesEnum.admin && role !== rolesEnum.juniorAdmin && role !== rolesEnum.seniorSeminarian) {
        return typesEnum.my;
    }

    const { seminarians, seminariansInCourses, childrens } = filters;

    if (seminarians?.length > 1 || seminariansInCourses?.length > 1 || childrens?.length > 1) return typesEnum.oneDay;
    if (seminarians?.length === 1) return typesEnum.oneWeek;
    
    return typesEnum.my;
};

const removeDuplicates = (array) => {
  const seen = new Map();
  return array.filter(item => {
    if (!seen.has(item.value)) {
      seen.set(item.value, true);
      return true;
    }
    return false;
  });
};

const normalizePeriodMode = (periodMode) =>
    Object.values(periodModes).includes(periodMode) ? periodMode : periodModes.week;

const getPeriodState = (periodMode) => {
    const current = dayjs();

    if (periodMode === periodModes.day) {
        return {
            day: current.format('YYYY-MM-DD'),
            weekRange: {
                start: current.startOf('week').format('YYYY-MM-DD'),
                end: current.endOf('week').format('YYYY-MM-DD')
            }
        };
    }

    if (periodMode === periodModes.month) {
        return {
            weekRange: {
                start: current.startOf('month').format('YYYY-MM-DD'),
                end: current.endOf('month').format('YYYY-MM-DD')
            },
            day: current.format('YYYY-MM-DD')
        };
    }

    return {
        weekRange: {
            start: current.startOf('week').format('YYYY-MM-DD'),
            end: current.endOf('week').format('YYYY-MM-DD')
        },
        day: current.format('YYYY-MM-DD')
    };
};

const initialState = {
    actions: [],
    reportsIdsForMassActions: [],
    applyNextForMassActions: false,
    periodMode: periodModes.week,
    isSingleDayView: false,
    activeTab: tabs.timetable,
    filters: {
        seminarians: [],
        childrens: [],
        groupsAndClasses:[],
        direction: null,
        seminariansInCourses: [],
        audiences: []
    },
    filtersSavedOptions: {
        optionsSeminarians: [],
        optionsChildrens: [],
        optionsGroupsAndClasses:[],
        optionsDirections: [],
    },
    weekRange: {
        start: dayjs().startOf('week').format('YYYY-MM-DD'),
        end: dayjs().endOf('week').format('YYYY-MM-DD')
    },
    day: dayjs().format('YYYY-MM-DD'),
    timetableData: [],
    timetableDataLoading: false,
    timetableDataError: null,
    audienceOptions: [],
    hasAudienceLessons: false,
    imageExportLoading: false,
    imageExportCustomTitle: '',
    imageExportUseCustomTitle: false,
    imageExportTimeRange: {
        from: null,
        to: null
    },
    imageExportUseTimeRange: false,
    workingHoursSelectedEmployees: {},
    workingHoursSelectedEmployeesLoading: false,
    workingHoursSelectedEmployeesError: null,
    type: typesEnum.my,
    intersectionCheck: {
        role: rolesEnum.children,
        range: {
            start: null,
            end: null
        },
    },
    filtersPlanning: {
        week: {
            monday: null,
            sunday: null
        },
        seminarian: null,
        direction: null,
        class: null,
        planLessThanTheFact: false
    }
};

const modalsSlice = createSlice({
    name: 'timetable',
    initialState,
    reducers: {
        setTimetableData: (state, action) => {
            state.timetableData = action.payload;
        },
        resetState: (state, action) => {
            const periodMode = normalizePeriodMode(
                action?.payload?.defaultPeriodMode ??
                (action?.payload?.defaultSingleDayView ? periodModes.day : initialState.periodMode)
            );
            return {
                    ...initialState,
                    ...getPeriodState(periodMode),
                    periodMode,
                    isSingleDayView: periodMode === periodModes.day,
                    type: determineTimetableType({
                        filters: initialState.filters,
                        currentType: initialState.type,
                        role: action?.payload?.role
                    })
            }
        },
        setTimetableDataLoading: (state, action) => {
            state.timetableDataLoading = action.payload;
        },
        setTimetableDataError: (state, action) => {
            state.timetableDataError = action.payload;
        },
        setAudienceFilterMetadata: (state, action) => {
            state.audienceOptions = action.payload?.options ?? [];
            state.hasAudienceLessons = !!action.payload?.hasAudienceLessons;
        },
        setImageExportLoading: (state, action) => {
            state.imageExportLoading = action.payload;
        },
        setImageExportCustomTitle: (state, action) => {
            state.imageExportCustomTitle = action.payload;
        },
        setImageExportUseCustomTitle: (state, action) => {
            state.imageExportUseCustomTitle = action.payload;
        },
        setImageExportTimeRange: (state, action) => {
            state.imageExportTimeRange = {
                ...state.imageExportTimeRange,
                ...action.payload
            };
        },
        setImageExportUseTimeRange: (state, action) => {
            state.imageExportUseTimeRange = action.payload;
        },
        setWorkingHoursSelectedEmployees: (state, action) => {
            state.workingHoursSelectedEmployees = action.payload;
        },
        setWorkingHoursSelectedEmployeesLoading: (state, action) => {
            state.workingHoursSelectedEmployeesLoading = action.payload;
        },
        setWorkingHoursSelectedEmployeesError: (state, action) => {
            state.workingHoursSelectedEmployeesError = action.payload;
        },
        addAction: (state, action) => { 
            state.actions = [...new Set([...state.actions, action.payload])];; 
        },
        deleteAction: (state, action) => { 
            state.actions = state.actions.filter((iterAction) => iterAction !== action.payload);
            state.reportsIdsForMassActions = initialState.reportsIdsForMassActions
        },
        toggleReportsIdsForMassActions: (state, action) => {
            if(state.reportsIdsForMassActions.includes(action.payload)){
                state.reportsIdsForMassActions = state.reportsIdsForMassActions.filter((iterReportsIdsForMassActions) => iterReportsIdsForMassActions !== action.payload); 
            }
            else{
                state.reportsIdsForMassActions = [...new Set([...state.reportsIdsForMassActions, action.payload])];
            }
        },
        resetReportsIdsForMassActions: (state, action) => {
            state.reportsIdsForMassActions = initialState.reportsIdsForMassActions
        },
        selectAllReportsIdsForMassActions: (state, action) => {
            const allReportIds = state.timetableData.flatMap((day) => 
                day.reports?.map((report) => report.id) ?? []
            );
            state.reportsIdsForMassActions = allReportIds;
        },
        toggleTab: (state, action) => {
            state.activeTab = action.payload; 
        },
        toggleFilter: (state, action ) => {
            state.filters = {
                ...state.filters,
                [action.payload.field]: action.payload.value !== undefined ? action.payload.value : null,
                ...action.payload.resetFields?.reduce((acc, key) => {
                    acc[key] = initialState.filters[key];
                    return acc;
                }, {})
            }; 
        },
        toggleFiltersSavedOptions: (state, action ) => {
            state.filtersSavedOptions = {
                ...state.filtersSavedOptions,
                [action.payload.field]: action.payload.value !== undefined ? 
                    removeDuplicates(
                        [...action.payload.value, ...state.filtersSavedOptions[action.payload.field]]
                    ) : [],
                ...action.payload.resetFields?.reduce((acc, key) => {
                    acc[key] = initialState.filtersSavedOptions[key];
                    return acc;
                }, {})
            }; 
        },
        filterAddMeeInSeminarians: (state, action) => {
            state.filters = {
                ...state.filters,
                seminarians: [...state.filters.seminarians, action.payload]
            }
        },
        resetFilter: (state, action) => {
            state.filters = initialState.filters 
        },
        setWeekRange: {
            reducer: (state, action) => {
                state.weekRange = action.payload;
            },
            prepare: (date) => {
                return {
                    payload: {
                        start: date.startOf('week').format('YYYY-MM-DD'),
                        end: date.endOf('week').format('YYYY-MM-DD')
                    }
                };
            }
        },
        setDay: {
            reducer: (state, action) => {
                state.day = action.payload;
            },
            prepare: (date) => {
                return {
                    payload: date.format('YYYY-MM-DD')
                };
            }
        },
        setType: (state, action) => {
            state.type = action.payload
        },
        setPeriodMode: (state, action) => {
            const nextPeriodMode = action.payload;
            const previousPeriodMode = state.periodMode;
            const referenceDate = previousPeriodMode === periodModes.day
                ? dayjs(state.day)
                : previousPeriodMode === periodModes.week
                    ? dayjs(state.weekRange.end)
                    : dayjs(state.weekRange.start);

            state.periodMode = nextPeriodMode;
            state.isSingleDayView = nextPeriodMode === periodModes.day;

            if (nextPeriodMode === periodModes.day) {
                state.day = previousPeriodMode === periodModes.day
                    ? referenceDate.format('YYYY-MM-DD')
                    : dayjs().format('YYYY-MM-DD');
            }

            if (nextPeriodMode === periodModes.week) {
                state.weekRange = {
                    start: referenceDate.startOf('week').format('YYYY-MM-DD'),
                    end: referenceDate.endOf('week').format('YYYY-MM-DD')
                };
            }

            if (nextPeriodMode === periodModes.month) {
                state.weekRange = {
                    start: referenceDate.startOf('month').format('YYYY-MM-DD'),
                    end: referenceDate.endOf('month').format('YYYY-MM-DD')
                };
            }
        },
        toggleIntersectionCheckRole: (state, action) => {
            state.intersectionCheck.role = action.payload; 
        },
        toggleIntersectionCheckRangeStartField: (state, action) => {
            state.intersectionCheck.range.start = action.payload
        },
        toggleIntersectionCheckRangeEndField: (state, action) => {
            state.intersectionCheck.range.end = action.payload
        },
        toggleWeekInFiltersPlanning: (state, action) => {
            state.filtersPlanning.week.monday = action.payload.monday
            state.filtersPlanning.week.sunday = action.payload.sunday
        },
        resetWeekInFiltersPlanning: (state, action) => {
            state.filtersPlanning.week.monday = initialState.filtersPlanning.week.monday
            state.filtersPlanning.week.sunday = initialState.filtersPlanning.week.sunday
        },
        toggleFiltersPlanning: (state, action ) => {
            state.filtersPlanning = {
                ...state.filtersPlanning,
                [action.payload.field]: action.payload.value !== undefined ? action.payload.value : null,
                ...action.payload.resetFields?.reduce((acc, key) => {
                    acc[key] = initialState.filtersPlanning[key];
                    return acc;
                }, {})
            }; 
        },
        togglePlanLessThanTheFactInFiltersPlanning: (state) => {
            state.filtersPlanning.planLessThanTheFact = !state.filtersPlanning.planLessThanTheFact; 
        },
        toggleApplyNextForMassActions: (state) => {
            state.applyNextForMassActions = !state.applyNextForMassActions; 
        },
        setMonthRange: {
            reducer: (state, action) => {
                state.weekRange = action.payload;
            },
            prepare: (date) => {
                return {
                    payload: {
                        start: date.startOf('month').format('YYYY-MM-DD'),
                        end: date.endOf('month').format('YYYY-MM-DD')
                    }
                };
            }
        },
    },
    extraReducers: (builder) => {
    // Автоматически сбрасываем фильтры при смене роли
    builder
    .addMatcher(
        (action) => 
            action.type === 'timetable/toggleFilter' || 
            action.type === 'timetable/resetFilter' ||
            action.type === 'impulse/profile/SET_USER_PROFILE' ||
            action.type === 'impulse/profile/UPD_USER_PROFILE',
        (state, action) => {
            // Для действий с профилем берем роль из payload
            const role = action?.profile?.role;

            const nextType = determineTimetableType(
                {
                    filters: state.filters, 
                    currentType: state.type,
                    role: role
                }
            );

            if (nextType === typesEnum.oneDay && state.type !== typesEnum.oneDay) {
                state.day = dayjs().format('YYYY-MM-DD');
            }

            state.type = nextType;
        }
    );
  }
});

const { actions, reducer } = modalsSlice;

export default reducer;

export const {
    addAction,
    deleteAction,
    toggleTab,
    toggleFilter,
    toggleFiltersSavedOptions,
    resetFilter,
    filterAddMeeInSeminarians,
    setWeekRange,
    setType,
    setPeriodMode,
    setDay,
    setMonthRange,
    toggleReportsIdsForMassActions,
    toggleIntersectionCheckRole,
    toggleIntersectionCheckRangeStartField,
    toggleIntersectionCheckRangeEndField,
    toggleWeekInFiltersPlanning,
    resetWeekInFiltersPlanning,
    toggleFiltersPlanning,
    togglePlanLessThanTheFactInFiltersPlanning,
    setTimetableData,
    setTimetableDataLoading,
    setTimetableDataError,
    setAudienceFilterMetadata,
    setImageExportLoading,
    setImageExportCustomTitle,
    setImageExportUseCustomTitle,
    setImageExportTimeRange,
    setImageExportUseTimeRange,
    setWorkingHoursSelectedEmployees,
    setWorkingHoursSelectedEmployeesError,
    setWorkingHoursSelectedEmployeesLoading,
    toggleApplyNextForMassActions,
    resetState,
    resetReportsIdsForMassActions,
    selectAllReportsIdsForMassActions
} = actions;
